import { supabase } from '@/integrations/supabase/client';
import { AdjustmentSettings } from '@/types/adjustment-filters';
import { calculateAdjustedPrices } from './adjustmentCalculations';

export interface EstimateExportData {
  estimate: any;
  customer: any;
  company: any;
  categorizedLineItems: CategoryGroup[];
  subtotal: number;
  discountAmount?: number;
  discountPercentage?: number;
  taxAmount: number;
  totalAmount: number;
  userProfile: any;
  documentDefaults: any;
}

export interface CategoryGroup {
  categoryName: string;
  lineItems: any[];
  categoryTotal: number;
}

export const getFinalLineItemAmount = (item: any): number => {
  // 1. Adjusted prices take highest priority (display-layer transform)
  if (item._adjustedTotalCost !== undefined) {
    return item._adjustedTotalCost;
  }

  // 2. Manual override
  if (item.manual_override && (item.manual_total_cost !== null && item.manual_total_cost !== undefined)) {
    return item.manual_total_cost;
  }
  
  // 3. Handle database format (current line items)
  if (item.total_cost_snapshot !== undefined) {
    return item.total_cost_snapshot || 0;
  }
  
  // 4. Handle snapshot format (version snapshots)
  if (item.total_cost !== undefined) {
    return item.total_cost || 0;
  }
  
  return 0;
};

export const getFinalLineItemUnitCost = (item: any): number => {
  // 1. Adjusted prices take highest priority (display-layer transform)
  if (item._adjustedUnitCost !== undefined) {
    return item._adjustedUnitCost;
  }

  // 2. Manual override — but NOT for custom items where manual_unit_cost stores base cost
  if (item.manual_override && item.component_type !== 'custom' && (item.manual_unit_cost !== null && item.manual_unit_cost !== undefined)) {
    return item.manual_unit_cost;
  }
  
  // 3. Handle database format (current line items)
  if (item.unit_cost_snapshot !== undefined) {
    return item.unit_cost_snapshot || 0;
  }
  
  // 4. Handle snapshot format (version snapshots)
  if (item.unit_cost !== undefined) {
    return item.unit_cost || 0;
  }
  
  return 0;
};

export const fetchEstimateExportData = async (estimateId: string, versionId?: string): Promise<EstimateExportData> => {
  console.log('[fetchEstimateExportData] Starting fetch for estimate:', estimateId);

  // Stage 1: Fetch estimate first (other queries depend on it)
  const { data: estimate, error: estimateError } = await supabase
    .from('estimates')
    .select('*')
    .eq('id', estimateId)
    .single();

  if (estimateError) {
    console.error('[fetchEstimateExportData] Error fetching estimate:', estimateError);
    throw new Error(`Failed to fetch estimate: ${estimateError.message}`);
  }

  // Stage 2: Parallel fetch all dependent data
  const [
    authResult,
    customerResult,
    companyResult,
    documentDefaultsResult,
    liveLineItemsResult,
    versionResult
  ] = await Promise.all([
    // Auth user
    supabase.auth.getUser(),
    // Customer (if exists)
    estimate.customer_id 
      ? supabase.from('customers').select('*').eq('id', estimate.customer_id).single()
      : Promise.resolve({ data: null, error: null }),
    // Company
    supabase.from('companies').select('*').eq('id', estimate.company_id).single(),
    // Document defaults
    supabase.from('document_defaults').select('*').eq('company_id', estimate.company_id).single(),
    // Live line items
    supabase.from('estimate_line_items').select('*').eq('estimate_id', estimateId).order('sort_order', { ascending: true }),
    // Version snapshot (if versionId provided)
    versionId 
      ? supabase.from('estimate_versions').select('line_items_snapshot').eq('id', versionId).single()
      : Promise.resolve({ data: null, error: null })
  ]);

  // Handle errors
  if (companyResult.error) {
    throw new Error(`Failed to fetch company: ${companyResult.error.message}`);
  }
  if (liveLineItemsResult.error) {
    throw new Error(`Failed to fetch line items: ${liveLineItemsResult.error.message}`);
  }
  if (versionId && versionResult.error) {
    throw new Error(`Failed to fetch version: ${versionResult.error.message}`);
  }

  const companyData = companyResult.data;
  const liveLineItems = liveLineItemsResult.data || [];
  const documentDefaults = documentDefaultsResult.data;
  const customer = customerResult.data;

  // Build user profile from auth + profile data
  let userProfile = null;
  const user = authResult.data?.user;
  if (user?.id) {
    const { data: profileData } = await supabase
      .from('profiles')
      .select('first_name, last_name, email, personal_phone_number, company_position')
      .eq('id', user.id)
      .single();
    
    if (profileData) {
      userProfile = { ...profileData, email: profileData.email || user.email };
    }
  }

  // Determine line items to process
  let lineItems: any[] = [];
  
  if (versionId && versionResult.data?.line_items_snapshot) {
    const snapshot = Array.isArray(versionResult.data.line_items_snapshot) 
      ? versionResult.data.line_items_snapshot 
      : [];
    
    if (snapshot.length > 0) {
      // Merge manual_description and category_id from live data
      lineItems = snapshot.map((snapshotItem: any) => {
        const liveItem = liveLineItems.find(li => li.id === snapshotItem.id);
        return {
          ...snapshotItem,
          manual_description: snapshotItem.manual_description ?? liveItem?.manual_description,
          category_id: snapshotItem.category_id ?? liveItem?.category_id
        };
      });
    } else {
      lineItems = liveLineItems;
    }
  } else {
    lineItems = liveLineItems;
  }

  // Stage 3: Batch fetch all component data (eliminates N+1 queries)
  const internalIds = lineItems
    .filter(i => i.component_type === 'internal_line_item' && i.component_id && !i.component)
    .map(i => i.component_id);
  const supplierIds = lineItems
    .filter(i => i.component_type === 'supplier_line_item' && i.component_id && !i.component)
    .map(i => i.component_id);
  const categoryIds = lineItems
    .filter(i => i.component_type === 'custom' && i.category_id)
    .map(i => i.category_id)
    .filter((id, index, arr) => arr.indexOf(id) === index); // Unique IDs

  const [internalResult, supplierResult, categoryResult] = await Promise.all([
    internalIds.length > 0 
      ? supabase.from('internal_line_items').select('*').in('id', internalIds)
      : Promise.resolve({ data: [] }),
    supplierIds.length > 0 
      ? (supabase as any).from('supplier_line_items').select('*').in('id', supplierIds)
      : Promise.resolve({ data: [] }),
    categoryIds.length > 0 
      ? supabase.from('supplier_categories').select('id, name').in('id', categoryIds)
      : Promise.resolve({ data: [] })
  ]);

  // Create lookup maps
  const internalMap = new Map((internalResult.data || []).map(i => [i.id, i]));
  const supplierMap = new Map((supplierResult.data || []).map(i => [i.id, i]));
  const categoryMap = new Map((categoryResult.data || []).map(i => [i.id, i]));

  // Process line items using maps (no await in loop)
  const processedLineItems = lineItems.map(item => {
    let componentData = item.component || null;
    
    if (!componentData) {
      if (item.component_type === 'internal_line_item' && item.component_id) {
        componentData = internalMap.get(item.component_id) || null;
      } else if (item.component_type === 'supplier_line_item' && item.component_id) {
        componentData = supplierMap.get(item.component_id) || null;
      } else if (item.component_type === 'custom') {
        const categoryName = item.category_id 
          ? categoryMap.get(item.category_id)?.name || 'Custom'
          : 'Custom';
        componentData = { category: categoryName };
      }
    }
    
    return {
      ...item,
      component: componentData,
      category: componentData?.category || item.category
    };
  });

  // Apply adjustments if configured
  const adjustmentSettings = estimate.adjustment_metadata as unknown as AdjustmentSettings | undefined;
  let finalLineItems = processedLineItems;

  if (adjustmentSettings && (adjustmentSettings.filters?.length > 0 || adjustmentSettings.discountPercentage)) {
    const adjustedPricesMap = await calculateAdjustedPrices(processedLineItems, adjustmentSettings);
    
    finalLineItems = processedLineItems.map(item => {
      const adjusted = adjustedPricesMap.get(item.id);
      if (adjusted) {
        return {
          ...item,
          _adjustedUnitCost: adjusted.adjustedUnitCost,
          _adjustedTotalCost: adjusted.adjustedTotalCost,
          _originalUnitCost: item.unit_cost_snapshot || item.unit_cost,
          _originalTotalCost: item.total_cost_snapshot || item.total_cost
        };
      }
      return item;
    });
  }

  // Helper to get final amount
  const getFinalAmount = (item: any): number => {
    return item._adjustedTotalCost !== undefined ? item._adjustedTotalCost : getFinalLineItemAmount(item);
  };

  // Categorize line items
  const materials = finalLineItems.filter(item => {
    const category = item.component?.category || item.category;
    return category === 'Materials';
  });
  const labours = finalLineItems.filter(item => {
    const category = item.component?.category || item.category;
    return category === 'Labour';
  });
  const others = finalLineItems.filter(item => {
    const category = item.component?.category || item.category;
    return category !== 'Materials' && category !== 'Labour';
  });

  const categorizedLineItems: CategoryGroup[] = [
    materials.length > 0 && { categoryName: 'Materials', lineItems: materials, categoryTotal: 0 },
    labours.length > 0 && { categoryName: 'Labour', lineItems: labours, categoryTotal: 0 },
    others.length > 0 && { categoryName: 'Other', lineItems: others, categoryTotal: 0 }
  ].filter(Boolean).map(category => ({
    ...category,
    categoryTotal: category.lineItems.reduce((sum, item) => sum + getFinalAmount(item), 0)
  }));

  // Calculate totals
  const subtotal = finalLineItems.reduce((sum, item) => sum + getFinalAmount(item), 0);
  
  let discountAmount = 0;
  let discountPercentage = 0;
  
  if (adjustmentSettings?.discountPercentage) {
    discountPercentage = adjustmentSettings.discountPercentage;
    discountAmount = subtotal * (discountPercentage / 100);
  }
  
  const subtotalAfterDiscount = subtotal - discountAmount;
  
  let taxAmount = 0;
  let totalAmount = subtotalAfterDiscount;
  
  const isVatRegistered = companyData?.is_vat_registered === true;
  const companyVatRate = companyData?.["VAT Rate"] || 15;
  
  if (isVatRegistered && companyVatRate > 0) {
    if (estimate.tax_inclusive) {
      taxAmount = (subtotalAfterDiscount * companyVatRate) / (100 + companyVatRate);
      totalAmount = subtotalAfterDiscount;
    } else {
      taxAmount = (subtotalAfterDiscount * companyVatRate) / 100;
      totalAmount = subtotalAfterDiscount + taxAmount;
    }
  }

  console.log('[fetchEstimateExportData] Export data prepared successfully');

  return {
    estimate,
    customer,
    company: companyData,
    categorizedLineItems,
    subtotal,
    discountAmount,
    discountPercentage,
    taxAmount,
    totalAmount,
    userProfile,
    documentDefaults
  };
};
